import React, { useState } from 'react';
import { ChevronLeft, MapPin, Mail, Phone, Shield, FileText, Lock } from 'lucide-react';

export default function Impressum({ onBack }) {
  const [expandedSection, setExpandedSection] = useState(null);

  const sections = [
    {
      id: 'company',
      title: 'Unternehmen',
      icon: MapPin,
      color: 'blue',
      content: [
        { label: 'Firma', value: 'IT-Seevetal' },
        { label: 'Inhaber', value: 'Sascha Elfert' },
        { label: 'Adresse', value: 'In der Guten Zeit 17, 21218 Seevetal' },
        { label: 'Land', value: 'Deutschland' },
      ]
    },
    {
      id: 'contact',
      title: 'Kontakt',
      icon: Mail,
      color: 'cyan',
      content: [
        { label: 'Telefon', value: '+49 4105 6925100' },
        { label: 'E-Mail', value: 'info@it-seevetal.de' },
        { label: 'Support', value: 'support@it-seevetal.de' },
      ]
    },
    {
      id: 'legal',
      title: 'Rechtliches',
      icon: FileText,
      color: 'purple',
      content: [
        { label: 'USt-IdNr.', value: 'wird eingefügt' },
        { label: 'Berufsbezeichnung', value: 'Softwareentwickler / IT-Dienstleistungen' },
        { label: 'Berufsgesetze', value: 'Bundestag der Bundesrepublik Deutschland' },
      ]
    }
  ];

  const policies = [
    {
      title: 'Datenschutz (DSGVO)',
      icon: Shield,
      description: 'Deine Daten sind sicher bei uns',
      items: [
        '✅ GDPR-konform',
        '✅ Deutsche Server',
        '✅ SSL/TLS Verschlüsselung',
        '✅ Deine Privatsphäre geschützt'
      ]
    },
    {
      title: 'Haftung & Nutzung',
      icon: Lock,
      description: 'Rechtliche Klarheit für dich',
      items: [
        '✅ Keine Haftung für externe Links',
        '✅ Inhalte regelmäßig geprüft',
        '✅ Urheberrecht geschützt',
        '✅ Änderungen jederzeit möglich'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 py-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6">
          <button 
            onClick={onBack} 
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-4 cursor-pointer hover:gap-3 transition-all"
          >
            <ChevronLeft className="w-4 h-4" />
            Zurück
          </button>
          <h1 className="text-4xl font-bold text-gray-900">Impressum & Datenschutz</h1>
          <p className="text-gray-600 mt-2">Rechtliche Informationen & Transparenz</p>
        </div>
      </header>

      <main className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Company Info Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-20">
            {sections.map((section) => {
              const Icon = section.icon;
              const colorMap = {
                blue: 'from-blue-500 to-blue-600',
                cyan: 'from-cyan-500 to-blue-500',
                purple: 'from-purple-500 to-blue-500'
              };
              
              return (
                <div key={section.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all">
                  {/* Header */}
                  <div className={`bg-gradient-to-r ${colorMap[section.color]} p-6 text-white`}>
                    <Icon className="w-8 h-8 mb-2" />
                    <h3 className="text-xl font-bold">{section.title}</h3>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 space-y-4">
                    {section.content.map((item, idx) => (
                      <div key={idx}>
                        <p className="text-sm font-medium text-gray-500 mb-1">{item.label}</p>
                        <p className="text-gray-900 font-semibold">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Policies Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 mb-12">Deine Rechte & Datenschutz</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {policies.map((policy, idx) => {
                const PolicyIcon = policy.icon;
                return (
                  <div key={idx} className="bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-lg transition-all">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <PolicyIcon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{policy.title}</h3>
                        <p className="text-sm text-gray-600">{policy.description}</p>
                      </div>
                    </div>
                    
                    <ul className="space-y-3">
                      {policy.items.map((item, i) => (
                        <li key={i} className="text-gray-700 text-sm flex items-start gap-2">
                          <span className="text-green-500 flex-shrink-0 mt-0.5">✓</span>
                          <span>{item.replace('✅ ', '')}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detailed Info Sections */}
          <div className="space-y-4 mb-20">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Detaillierte Informationen</h2>
            
            {[
              {
                title: 'Cookie-Policy & Google Analytics',
                content: 'Wir nutzen Google Analytics (Tracking-ID: G-CJVX7L79BP) um deine Website-Nutzung zu verstehen. Analytics erfasst KEINE persönlichen Daten – nur: Seitenaufrufe, Verweildauer, Gerätetyp, Browser, ungefähre geografische Lage. Alle Daten sind anonymisiert. Du kannst Cookies jederzeit ablehnen oder im Browser löschen. Mit der Akzeptance des Cookie-Banners stimmst du Google Analytics zu. Du kannst deine Zustimmung jederzeit widerrufen.'
              },
              {
                title: 'Haftungsausschluss',
                content: 'Die Inhalte dieser Website werden mit größter Sorgfalt erstellt. Wir übernehmen jedoch keine Gewähr für die Richtigkeit, Vollständigkeit oder Aktualität der bereitgestellten Informationen. Externe Links werden regelmäßig überprüft, eine Haftung für deren Inhalte lehnen wir jedoch ab. Bei Rechtsverletzungen kontaktieren Sie uns bitte sofort.'
              },
              {
                title: 'Datenschutzerklärung & Cookies',
                content: 'Dein Datenschutz ist uns wichtig. Wir nutzen Google Analytics (anonymisiert) um die Website zu verbessern. Keine persönlichen Daten werden erfasst – nur: Seitenaufrufe, Besuchsdauer, Gerätetyp, Browser. SSL-Verschlüsselung, deutsche Server, GDPR-konform. Deine E-Mail wird nur für Support genutzt. Du kannst Cookies jederzeit widerrufen. Du hast das Recht, deine Daten einzusehen, zu ändern oder löschen zu lassen (Kontakt: info@it-seevetal.de).'
              },
              {
                title: 'Urheberrecht',
                content: 'Alle Inhalte dieser Website (Texte, Bilder, Code) sind urheberrechtlich geschützt. Eine Vervielfältigung oder Weiterverbreitung ohne schriftliche Genehmigung ist nicht gestattet. Ausnahme: Private, nicht-kommerzielle Nutzung ist erlaubt.'
              },
              {
                title: 'Kontakt bei Fragen',
                content: 'Hast du Fragen zu dieser Seite, zum Datenschutz oder deinen Rechten? Schreib uns einfach: info@it-seevetal.de. Wir antworten schnell und helfen gerne weiter.'
              }
            ].map((section, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setExpandedSection(expandedSection === idx ? null : idx)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition text-left"
                >
                  <h3 className="font-bold text-gray-900 text-lg">{section.title}</h3>
                  <span className={`text-gray-400 transition-transform ${expandedSection === idx ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                
                {expandedSection === idx && (
                  <div className="px-6 pb-4 border-t border-gray-200 pt-4">
                    <p className="text-gray-700 leading-relaxed">{section.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Noch Fragen?</h3>
            <p className="text-gray-700 mb-6">Wir helfen dir gerne weiter und beantworten alle deine Fragen zum Datenschutz und deinen Rechten.</p>
            <button 
              onClick={onBack}
              className="inline-block px-8 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg hover:shadow-lg transition font-medium cursor-pointer"
            >
              Kontakt aufnehmen
            </button>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-6 mt-24">
        <div className="max-w-6xl mx-auto text-center text-sm">
          <p>© 2026 IT-Seevetal. Alle Rechte vorbehalten.</p>
        </div>
      </footer>
    </div>
  );
}